import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { DeleteOutlineRounded } from "@mui/icons-material";

import DocumentUploadZone from "pages/dashboard/admin/prompts/components/DocumentUploadZone";
import ConfirmDialog from "components/common/ConfirmDialog";
import DataLoadErrorAlert from "components/common/DataLoadErrorAlert";
import ImportFromDocumentsDialog from "components/dashboard/healthRecords/ImportFromDocumentsDialog";
import { AuthContext } from "context/Auth";
import firebase from "firebase/compat/app";
import { db } from "services/firebase";
import {
  ACCEPTED_EXTENSIONS,
  checkConsistency,
  deleteDocument,
  getCachedDocuments,
  getSettledLatestCheck,
  listDocuments,
  subscribeToLatestCheck,
  uploadDocument,
} from "services/patientDocumentsService";
import {
  applyProposals,
  buildProposals,
  extractFromDocuments,
  findForeignDocuments,
} from "services/healthRecordImportService";

/** The extension list in words a patient recognizes. */
const ACCEPTED_LABEL = "PDF, Word, PowerPoint, or text files, up to 5 MB each";

/**
 * Only the identity check runs for patients. The value and contradiction
 * checks speak to clinicians ("Potassium reported as 42 mmol/L"), give the
 * patient nothing to act on, and alarm them about things that are usually
 * benign. A document that may be someone else's is the one finding a patient
 * can do something about: delete it.
 */
const PATIENT_CHECKS = ["identity"];

/**
 * One bar across the whole run, so it never disappears or restarts. Each phase
 * owns a slice: uploads by file count, the check by its own reported progress,
 * and the final read (a single call with no progress of its own) eases toward
 * the end of its slice while it waits.
 */
const SLICES = {
  uploading: [0, 25],
  checking: [25, 60],
  finding: [60, 97],
};

function sliceValue(phase, fraction) {
  const [start, end] = SLICES[phase];
  return start + (end - start) * Math.min(1, Math.max(0, fraction));
}

/**
 * Patient-uploaded documents, all in one dialog. Each upload is extracted to
 * flat text server-side, and that text is appended to the chat system prompt.
 *
 * Adding files runs the whole pipeline with no further buttons: upload, the
 * identity check, then a read of the documents for MasterChart values.
 * Nothing reaches the record without the patient's OK, because the extraction
 * is a model and the record is what providers see; it arrives as one review
 * step, presented as the thing left to do rather than an optional extra.
 *
 * Interruption, by stage:
 * - Processing is orchestrated from the browser, so it can't survive the page
 *   going away. The dialog can't be closed mid-run and the tab warns before
 *   unloading. Every call in the run has a hard timeout, so this can't trap
 *   anyone indefinitely.
 * - Review is saved: the extraction lands on the user doc as
 *   `pendingDocumentImport`, and proposals are rebuilt from it against the
 *   live record. "Not now", a reload, or leaving the page all leave it
 *   waiting, and `onStatusChange` lets the hub row say so.
 */
/**
 * Whether a proposal is worth putting in front of the patient. New values and
 * changes to existing ones both need a decision; a duplicate of something
 * already on file doesn't. Changes arrive unticked, so counting `selected`
 * would hide them whenever nothing else was new.
 */
const needsReview = (p) =>
  !(p.conflict && p.current === "Already in your record");

const DocumentsDialog = ({ open, onClose, onImported, onStatusChange }) => {
  const { user, userData, medications } = useContext(AuthContext);
  const cached = getCachedDocuments();
  const [documents, setDocuments] = useState(cached?.documents ?? []);
  const [maxDocuments, setMaxDocuments] = useState(cached?.maxDocuments ?? 0);
  const [loading, setLoading] = useState(!cached);
  const [loadError, setLoadError] = useState(null);
  // null | "uploading" | "checking" | "finding"
  const [phase, setPhase] = useState(null);
  const [uploadFraction, setUploadFraction] = useState(0);
  // The files in this run: names until they upload, then their document ids.
  // Their rows show a spinner where the delete button goes.
  const [inFlight, setInFlight] = useState({ names: [], ids: [] });
  const [findingFraction, setFindingFraction] = useState(0);
  const [checkState, setCheckState] = useState(null);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [success, setSuccess] = useState(null);
  // Delete goes through one confirm whether it's a row's trash icon or the
  // selection toolbar, as on the admin All Files page.
  const [deleteTargets, setDeleteTargets] = useState([]);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [deleting, setDeleting] = useState(false);
  const pending = userData?.pendingDocumentImport ?? null;
  const extraction = pending?.extraction ?? null;
  // Rebuilt against the live record, so anything added since the run (here or
  // on the record pages) shows as a duplicate rather than being added twice.
  const proposals = useMemo(() => {
    if (!extraction) return [];
    const excluded = new Set(pending?.excludedFileNames || []);
    return buildProposals(
      extraction,
      userData?.healthRecords,
      medications,
    ).filter((p) => !excluded.has(p.sourceDocument));
  }, [extraction, pending, userData?.healthRecords, medications]);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reviewError, setReviewError] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const result = await listDocuments();
      setDocuments(result.documents);
      setMaxDocuments(result.maxDocuments);
      setLoadError(null);
    } catch (err) {
      setLoadError(err?.message || "Could not load your documents.");
    } finally {
      setLoading(false);
    }
  }, []);

  // Prefetched when the hub mounts (this component lives there, closed), so
  // the dialog opens with the list already in hand. A cache hit costs nothing;
  // a miss is one call per page load, which also warms a cold function.
  useEffect(() => {
    if (loading) refresh();
    // Mount only: a retry after a load error calls refresh() itself.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!open && !phase) return undefined;
    return subscribeToLatestCheck(
      (state) => setCheckState(state),
      () => {},
    );
  }, [open, phase]);

  useEffect(() => {
    if (phase !== "finding") return undefined;
    setFindingFraction(0);
    const timer = setInterval(
      () => setFindingFraction((f) => f + (1 - f) * 0.06),
      1000,
    );
    return () => clearInterval(timer);
  }, [phase]);

  const suggestionCount = proposals.filter(needsReview).length;

  useEffect(() => {
    if (!phase) setInFlight({ names: [], ids: [] });
  }, [phase]);

  // Leaving mid-run loses it, so the browser asks first.
  useEffect(() => {
    if (!phase) return undefined;
    const warn = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [phase]);

  useEffect(() => {
    onStatusChange?.({ busy: Boolean(phase), suggestionCount });
  }, [phase, suggestionCount, onStatusChange]);

  const findSuggestions = async (exclude) => {
    if (!user?.uid) return 0;
    setPhase("finding");
    const snap = await db.collection("users").doc(user.uid).get();
    const current = snap.data()?.healthRecords || {};
    const result = await extractFromDocuments(current, exclude);
    const found = result.proposals.filter(needsReview).length;
    // Replaces whatever the last run left waiting. The JSON round trip drops
    // undefined values, which Firestore rejects.
    await db
      .collection("users")
      .doc(user.uid)
      .update({
        pendingDocumentImport: found
          ? {
              extraction: JSON.parse(JSON.stringify(result.extraction)),
              excludedFileNames: exclude.fileNames || [],
              createdAt: firebase.firestore.FieldValue.serverTimestamp(),
            }
          : firebase.firestore.FieldValue.delete(),
      });
    return found;
  };

  // A run in progress is marked on the user doc, so one the page never got to
  // finish (closed tab, reload) picks up on the next MasterChart visit. Client
  // clock, so comparisons against server times allow some skew.
  const setRunMarker = (startedAt) =>
    db
      .collection("users")
      .doc(user.uid)
      .update({
        documentImportRun: startedAt
          ? { startedAt }
          : firebase.firestore.FieldValue.delete(),
      })
      .catch(() => {});

  /**
   * Everything after the uploads: the identity check, then the read for
   * MasterChart details. A resumed run reuses a check that finished on the
   * server after the run began instead of paying for it twice.
   */
  const analyze = async ({
    saved,
    truncated = "",
    runStartedAt,
    resumed = false,
  }) => {
    try {
      let check = null;
      try {
        setPhase("checking");
        if (resumed) check = await getSettledLatestCheck(runStartedAt - 60000);
        if (!check) check = await checkConsistency({ checks: PATIENT_CHECKS });
      } catch (err) {
        // Without the identity check there is no way to know the documents
        // are all this patient's, so no suggestions rather than unchecked ones.
        setNotice(
          `${saved} We couldn't look for details to add to your MasterChart this time.`,
        );
        return;
      }

      const foreign = findForeignDocuments(check);
      if (foreign.ambiguous) {
        // The warning below the list says which documents and what to do.
        setNotice(`${saved} We didn't add anything to your MasterChart yet.`);
        return;
      }

      try {
        const found = await findSuggestions(foreign);
        // Review is the step left, so go straight to it rather than making
        // the patient find a button. "Not now" there leaves the button here.
        if (found) {
          setReviewError(null);
          setReviewOpen(true);
          onClose();
        } else {
          setSuccess(`${saved}${truncated}`);
        }
      } catch (err) {
        setNotice(
          `${saved} We couldn't look for details to add to your MasterChart this time.`,
        );
      }
    } finally {
      await setRunMarker(null);
      setPhase(null);
    }
  };

  const handleFilesAccepted = async (files) => {
    setPhase("uploading");
    setUploadFraction(0);
    setInFlight({ names: files.map((f) => f.name), ids: [] });
    setError(null);
    setNotice(null);
    setSuccess(null);
    const runStartedAt = Date.now();
    // This tab owns the run; never resume it here as if it were interrupted.
    resumedRef.current = true;

    // Sequential rather than parallel: each upload holds its whole file in
    // memory as base64, and the count cap is enforced per call server-side.
    const uploaded = [];
    const failures = [];
    for (const file of files) {
      try {
        uploaded.push(await uploadDocument(file));
        // Marked once something is saved: an interrupted run with nothing
        // uploaded has nothing new to analyze.
        if (uploaded.length === 1) await setRunMarker(runStartedAt);
      } catch (err) {
        failures.push(`${file.name}: ${err?.message || "upload failed"}`);
      }
      setUploadFraction((uploaded.length + failures.length) / files.length);
    }
    await refresh();
    setInFlight({ names: [], ids: uploaded.map((d) => d.docId) });

    if (failures.length) setError(failures.join(" "));
    if (!uploaded.length) {
      setPhase(null);
      return;
    }

    await analyze({
      saved: `Your document${uploaded.length === 1 ? " is" : "s are"} saved and will be used in chat.`,
      truncated: uploaded.some((d) => d.truncated)
        ? " Long documents are cut short, so only the beginning is used."
        : "",
      runStartedAt,
    });
  };

  // Resume an interrupted run once per mount, in the background: the row on
  // the hub shows it working, and the review opens when it's done.
  const resumedRef = useRef(false);
  const interruptedRun = userData?.documentImportRun?.startedAt ?? null;
  useEffect(() => {
    if (!interruptedRun || phase || resumedRef.current || !user?.uid) return;
    resumedRef.current = true;
    analyze({
      saved: "Your documents are saved and will be used in chat.",
      runStartedAt: interruptedRun,
      resumed: true,
    });
    // analyze is recreated every render; this keys off the marker alone.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interruptedRun, phase, user?.uid]);

  const toggleSelected = (id) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const handleConfirmDelete = async () => {
    setDeleting(true);
    setError(null);
    setSuccess(null);
    // One at a time, carrying on past a failure so one bad delete doesn't
    // leave the rest untouched.
    const deleted = [];
    const failures = [];
    for (const doc of deleteTargets) {
      try {
        await deleteDocument(doc.id);
        deleted.push(doc);
      } catch (err) {
        failures.push(
          `${doc.fileName}: ${err?.message || "could not be deleted"}`,
        );
      }
    }
    try {
      await refresh();
      // A suggestion from a deleted document has nothing left to back it.
      if (deleted.length && pending && user?.uid) {
        await db
          .collection("users")
          .doc(user.uid)
          .update({
            "pendingDocumentImport.excludedFileNames":
              firebase.firestore.FieldValue.arrayUnion(
                ...deleted.map((d) => d.fileName),
              ),
          });
      }
    } catch (err) {
      failures.push(err?.message || "Could not refresh your documents.");
    }
    setSelectedIds((prev) => {
      const next = new Set(prev);
      deleted.forEach((d) => next.delete(d.id));
      return next;
    });
    if (deleted.length) {
      setSuccess(
        deleted.length === 1
          ? `Deleted "${deleted[0].fileName}".`
          : `Deleted ${deleted.length} documents.`,
      );
    }
    if (failures.length) setError(failures.join(" "));
    setDeleteTargets([]);
    setDeleting(false);
  };

  const openReview = () => {
    setReviewError(null);
    setReviewOpen(true);
    onClose();
  };

  const handleApply = async (approved) => {
    if (!user?.uid || !approved.length) return;
    setSaving(true);
    setReviewError(null);
    try {
      // Read fresh rather than from AuthContext: the merge writes the whole
      // object back, and a stale copy would drop edits made on the record pages.
      const snap = await db.collection("users").doc(user.uid).get();
      const merged = applyProposals(approved, snap.data()?.healthRecords || {});
      await db.collection("users").doc(user.uid).update({
        healthRecords: merged,
        pendingDocumentImport: firebase.firestore.FieldValue.delete(),
      });
      setReviewOpen(false);
      onImported?.(
        `Added ${approved.length} item${approved.length === 1 ? "" : "s"} to your MasterChart.`,
      );
    } catch (err) {
      setReviewError(err?.message || "Could not save to your record.");
    } finally {
      setSaving(false);
    }
  };

  const checkProgress = checkState?.progress;
  const progressValue =
    phase === "uploading"
      ? sliceValue("uploading", uploadFraction)
      : phase === "checking"
        ? sliceValue(
            "checking",
            checkProgress?.total > 0
              ? checkProgress.completed / checkProgress.total
              : 0,
          )
        : phase === "finding"
          ? sliceValue("finding", findingFraction)
          : 0;

  // Identity findings for the documents on file now. Once the set changes
  // they no longer describe it, and with no manual re-run they'd only be noise.
  const checkedIds = checkState?.documentIds ?? [];
  const currentIds = documents.map((doc) => doc.id);
  const findingsCurrent =
    !phase &&
    checkState?.status === "completed" &&
    checkedIds.length === currentIds.length &&
    checkedIds.every((id) => currentIds.includes(id));
  const identityIssues = findingsCurrent
    ? (checkState.issues || []).filter(
        (issue) => issue.check === "identity" && issue.severity === "high",
      )
    : [];
  const flaggedNames = [
    ...new Set(
      identityIssues
        .filter((issue) => (issue.docIds || []).length === 1)
        .map((issue) => issue.docIds[0]),
    ),
  ];
  const mixedIdentity = identityIssues.some(
    (issue) => (issue.docIds || []).length > 1,
  );

  const atLimit = maxDocuments > 0 && documents.length >= maxDocuments;

  const selectableIds = documents
    .filter((d) => !inFlight.ids.includes(d.id))
    .map((d) => d.id);
  const allSelected =
    selectableIds.length > 0 &&
    selectableIds.every((id) => selectedIds.has(id));
  const someSelected =
    !allSelected && selectableIds.some((id) => selectedIds.has(id));

  // Files still uploading go on top; uploaded ones from this run keep a
  // spinner until the run ends.
  const rows = [
    ...inFlight.names.map((name, i) => ({
      key: `uploading-${i}`,
      fileName: name,
      busy: true,
    })),
    ...documents.map((doc) => ({
      ...doc,
      key: doc.id,
      busy: inFlight.ids.includes(doc.id),
    })),
  ];

  return (
    <>
      <Dialog
        open={open}
        // Nothing closes it mid-run; see the note on interruption above.
        onClose={phase ? undefined : onClose}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>My documents</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            Add records like a discharge summary or lab report. The assistant
            uses them to answer your questions in chat, and we'll suggest
            details to add to your MasterChart.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Photos and scanned pages can't be read yet.
          </Typography>

          {loadError ? (
            <DataLoadErrorAlert
              message={loadError}
              onRetry={() => {
                setLoading(true);
                refresh();
              }}
            />
          ) : null}
          {error ? (
            <Alert
              severity="error"
              onClose={() => setError(null)}
              sx={{ mb: 2 }}
            >
              {error}
            </Alert>
          ) : null}
          {success ? (
            <Alert
              severity="success"
              onClose={() => setSuccess(null)}
              sx={{ mb: 2 }}
            >
              {success}
            </Alert>
          ) : null}
          {notice ? (
            <Alert
              severity="info"
              onClose={() => setNotice(null)}
              sx={{ mb: 2 }}
            >
              {notice}
            </Alert>
          ) : null}
          {!phase && suggestionCount > 0 ? (
            <Alert
              severity="info"
              sx={{ mb: 2 }}
              action={
                <Button color="inherit" size="small" onClick={openReview}>
                  Review
                </Button>
              }
            >
              Your documents are saved. We found {suggestionCount} detail
              {suggestionCount === 1 ? "" : "s"} for your MasterChart. Please
              review {suggestionCount === 1 ? "it" : "them"} so we can add{" "}
              {suggestionCount === 1 ? "it" : "them"}.
            </Alert>
          ) : null}

          {phase ? (
            // Takes the drop zone's place, so there is one thing on screen
            // saying what is happening and nowhere to drop more files.
            <Paper
              variant="outlined"
              sx={{ p: 3, mb: 3, textAlign: "center", borderStyle: "dashed" }}
            >
              <Typography variant="subtitle1" sx={{ mb: 1.5 }}>
                Processing your documents…
              </Typography>
              <Box sx={{ maxWidth: 320, mx: "auto" }}>
                <LinearProgress variant="determinate" value={progressValue} />
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                This can take a few minutes. Please keep this page open.
              </Typography>
            </Paper>
          ) : atLimit ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              You've reached the limit of {maxDocuments} documents. Please
              delete one to add another.
            </Alert>
          ) : (
            <DocumentUploadZone
              inputId="patient-document-upload"
              acceptedExtensions={ACCEPTED_EXTENSIONS}
              acceptedLabel={ACCEPTED_LABEL}
              disabled={loading || Boolean(loadError)}
              onFilesAccepted={handleFilesAccepted}
              // Its built-in bottom margin only makes sense with the list
              // under it; alone it stacks on the dialog's own padding.
              sx={rows.length || loading ? undefined : { mb: 0 }}
            />
          )}

          {flaggedNames.length ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {flaggedNames.length === 1
                ? `"${flaggedNames[0]}" may be about someone else, so we left it out of your MasterChart. If it isn't yours, please delete it.`
                : `These documents may be about someone else, so we left them out of your MasterChart: ${flaggedNames.join(", ")}. If they aren't yours, please delete them.`}
            </Alert>
          ) : null}
          {mixedIdentity ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Your documents don't all seem to be about the same person. Please
              check that each one is yours and delete any that aren't.
            </Alert>
          ) : null}

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : rows.length ? (
            // Stock MUI selection table at default density: checkbox column
            // with select-all in the header, whose title swaps to a count and
            // a delete button while anything is selected. The outer columns
            // sit flush with the dialog's text edge instead of inset.
            <>
              <TableContainer>
                {/* Fixed layout so a long file name ellipsizes instead of
                    pushing the action column off the edge. */}
                <Table
                  sx={{
                    tableLayout: "fixed",
                    "& .MuiTableCell-paddingCheckbox": { pl: 0, width: 42 },
                    "& .MuiTableCell-root:last-of-type": { pr: 0 },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          edge="start"
                          indeterminate={someSelected}
                          checked={allSelected}
                          onChange={() =>
                            setSelectedIds(
                              allSelected ? new Set() : new Set(selectableIds),
                            )
                          }
                          disabled={Boolean(phase) || !selectableIds.length}
                          inputProps={{ "aria-label": "Select all" }}
                        />
                      </TableCell>
                      <TableCell>
                        {selectedIds.size > 0
                          ? `${selectedIds.size} selected`
                          : "Your documents"}
                      </TableCell>
                      <TableCell
                        align="right"
                        padding="none"
                        sx={{ width: 56 }}
                      >
                        {selectedIds.size > 0 ? (
                          <Tooltip title="Delete selected">
                            <span>
                              <IconButton
                                onClick={() =>
                                  setDeleteTargets(
                                    documents.filter((d) =>
                                      selectedIds.has(d.id),
                                    ),
                                  )
                                }
                                disabled={deleting || Boolean(phase)}
                                aria-label="Delete selected"
                              >
                                <DeleteOutlineRounded />
                              </IconButton>
                            </span>
                          </Tooltip>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((doc) => {
                      const isSelected = selectedIds.has(doc.id);
                      return (
                        <TableRow
                          key={doc.key}
                          hover
                          selected={isSelected}
                          onClick={
                            doc.busy || phase
                              ? undefined
                              : () => toggleSelected(doc.id)
                          }
                          sx={{
                            cursor: doc.busy || phase ? "default" : "pointer",
                          }}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              edge="start"
                              checked={isSelected}
                              disabled={doc.busy || Boolean(phase)}
                              inputProps={{
                                "aria-label": `Select ${doc.fileName}`,
                              }}
                            />
                          </TableCell>
                          <TableCell
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {doc.fileName}
                          </TableCell>
                          <TableCell align="right" padding="none">
                            {doc.busy ? (
                              // Same footprint as the delete button, so rows
                              // don't shift when it comes back.
                              <Box
                                sx={{
                                  width: 40,
                                  height: 40,
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                <CircularProgress size={18} />
                              </Box>
                            ) : (
                              <Tooltip title="Delete">
                                <span>
                                  <IconButton
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      setDeleteTargets([doc]);
                                    }}
                                    disabled={Boolean(phase)}
                                    aria-label={`Delete ${doc.fileName}`}
                                  >
                                    <DeleteOutlineRounded fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={Boolean(phase)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={deleteTargets.length > 0}
        onClose={() => setDeleteTargets([])}
        title={
          deleteTargets.length > 1
            ? `Delete ${deleteTargets.length} documents?`
            : "Delete this document?"
        }
        message={
          deleteTargets.length > 1
            ? `These ${deleteTargets.length} documents will be permanently removed and will no longer be used in chat.`
            : deleteTargets.length
              ? `"${deleteTargets[0].fileName}" will be permanently removed and will no longer be used in chat.`
              : ""
        }
        confirmLabel="Delete"
        confirmColor="error"
        loading={deleting}
        onConfirm={handleConfirmDelete}
      />

      <ImportFromDocumentsDialog
        open={reviewOpen}
        onClose={() => setReviewOpen(false)}
        loading={false}
        error={reviewError}
        extraction={extraction}
        proposals={proposals}
        saving={saving}
        onApply={handleApply}
      />
    </>
  );
};

export default DocumentsDialog;
